import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// Check if subscription should be marked as completed
export const checkAndUpdateSubscriptionStatus = async (subscriptionId) => {
  try {
    const subscriptionRef = doc(db, "subscriptions", subscriptionId);
    const subscriptionSnap = await getDoc(subscriptionRef);
    
    if (!subscriptionSnap.exists()) {
      return { shouldUpdate: false, reason: "Subscription not found" };
    }

    const subscription = subscriptionSnap.data();
    
    // If already completed, no need to update
    if (subscription.status === "completed") {
      return { shouldUpdate: false, reason: "Already completed" };
    }

    // Calculate delivery days (excluding Mondays)
    const startDate = new Date(subscription.startDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let deliveryDays = 0;
    let currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);
    
    while (currentDate <= today) {
      // Skip Mondays (holidays)
      if (currentDate.getDay() !== 1) {
        deliveryDays++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Check if 26 delivery days are completed
    const targetDeliveryDays = subscription.deliveryDays || 26;
    
    if (deliveryDays >= targetDeliveryDays) {
      // Update subscription status to completed
      await updateDoc(subscriptionRef, {
        status: "completed",
        completedAt: new Date(),
        actualDeliveryDays: deliveryDays,
        endDate: today.toISOString().split('T')[0]
      });
      
      return { 
        shouldUpdate: true, 
        reason: "26 delivery days completed",
        deliveryDays,
        targetDeliveryDays
      };
    }
    
    return { 
      shouldUpdate: false, 
      reason: "Not enough delivery days",
      deliveryDays,
      targetDeliveryDays
    };
    
  } catch (error) {
    console.error("Error checking subscription status:", error);
    return { shouldUpdate: false, reason: "Error occurred", error };
  }
};

// Get subscription status with completion check
export const getSubscriptionStatus = async (subscriptionId) => {
  try {
    const subscriptionRef = doc(db, "subscriptions", subscriptionId);
    const subscriptionSnap = await getDoc(subscriptionRef);
    
    if (!subscriptionSnap.exists()) {
      return { status: "not_found", subscription: null };
    }

    const subscription = subscriptionSnap.data();
    
    // If already completed, return as is
    if (subscription.status === "completed") {
      return { status: "completed", subscription };
    }
    
    // Check if should be completed
    const completionCheck = await checkAndUpdateSubscriptionStatus(subscriptionId);
    
    if (completionCheck.shouldUpdate) {
      // Re-fetch the updated subscription
      const updatedSnap = await getDoc(subscriptionRef);
      return { 
        status: "completed", 
        subscription: updatedSnap.data(),
        completionInfo: completionCheck
      };
    }
    
    return { status: subscription.status, subscription };
    
  } catch (error) {
    console.error("Error getting subscription status:", error);
    return { status: "error", subscription: null, error };
  }
};

// Calculate remaining delivery days
export const calculateRemainingDeliveryDays = (subscription) => {
  if (!subscription || subscription.status === "completed") {
    return 0;
  }

  const startDate = new Date(subscription.startDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  let deliveryDays = 0;
  let currentDate = new Date(startDate);
  currentDate.setHours(0, 0, 0, 0);
  
  while (currentDate <= today) {
    // Skip Mondays (holidays)
    if (currentDate.getDay() !== 1) {
      deliveryDays++;
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }

  const targetDeliveryDays = subscription.deliveryDays || 26;
  const remaining = Math.max(0, targetDeliveryDays - deliveryDays);
  
  return {
    completed: deliveryDays,
    remaining,
    total: targetDeliveryDays,
    isCompleted: deliveryDays >= targetDeliveryDays
  };
}; 